/**
 * Comprehensive sharing utility with iframe and feature policy support
 * Handles Web Share API restrictions and provides fallback methods
 */

export const isWebShareSupported = () => {
  try {
    // Check if Web Share API is supported
    if (!navigator.share) {
      return false;
    }

    // Check if running in iframe context
    if (window.self !== window.top) {
      // In iframe - check feature policy
      if (document.featurePolicy) {
        return document.featurePolicy.allowsFeature('web-share');
      }
      // If no feature policy support, assume blocked in iframe
      return false;
    }

    return true;
  } catch (error) {
    console.warn('Error checking Web Share API support:', error);
    return false;
  }
};

export const isClipboardSupported = () => {
  try {
    return navigator.clipboard && navigator.clipboard.writeText;
  } catch (error) {
    console.warn('Error checking Clipboard API support:', error);
    return false;
  }
};

export const copyToClipboard = async (text) => {
  try {
    if (isClipboardSupported()) {
      await navigator.clipboard.writeText(text);
      return { success: true, method: 'clipboard' };
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return { success: true, method: 'fallback' };
      } catch (err) {
        document.body.removeChild(textArea);
        throw err;
      }
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return { success: false, error: error.message };
  }
};

export const generateSocialShareUrls = (url, title, text) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedText}%20${encodedUrl}`
  };
};

export const shareContent = async (shareData, options = {}) => {
  const {
    title = 'Check this out',
    text = 'Take a look at this',
    url = window.location.href,
    fallbackMessage = 'Link copied to clipboard!',
    onSuccess = () => {},
    onError = () => {},
    showSocialOptions = false
  } = { ...shareData, ...options };

  try {
    // First try Web Share API if supported and allowed
    if (isWebShareSupported()) {
      await navigator.share({ title, text, url });
      onSuccess({ method: 'native', message: 'Shared successfully' });
      return { success: true, method: 'native' };
    }

    // If social options are requested, return social URLs
    if (showSocialOptions) {
      const socialUrls = generateSocialShareUrls(url, title, text);
      onSuccess({ method: 'social', socialUrls });
      return { success: true, method: 'social', socialUrls };
    }

    // Fallback to clipboard
    const clipboardResult = await copyToClipboard(url);
    if (clipboardResult.success) {
      onSuccess({ method: 'clipboard', message: fallbackMessage });
      return { success: true, method: 'clipboard' };
    }

    throw new Error('All sharing methods failed');

  } catch (error) {
    console.error('Error sharing content:', error);
    
    // Final fallback - try clipboard one more time
    try {
      const clipboardResult = await copyToClipboard(url);
      if (clipboardResult.success) {
        onSuccess({ method: 'clipboard', message: fallbackMessage });
        return { success: true, method: 'clipboard' };
      }
    } catch (finalError) {
      console.error('Final clipboard fallback failed:', finalError);
    }

    onError({ error: error.message });
    return { success: false, error: error.message };
  }
};

export const createShareHandler = (shareData, options = {}) => {
  return async () => {
    return await shareContent(shareData, options);
  };
};